import React from 'react';
import pick from 'object.pick';
import { v4 as genId } from 'uuid';

import { connect, Controller } from '@stubhub/react-store-provider';

import {
  filterByEventId,
  objectHasEventId,
  selectCurrencyCode,
  selectEventOriginCountry
} from '../withEvents/selectors';

import {
  checkTicketForRowError,
  checkTicketForSeatRowError,
  ticketsSectionRowSeatsValid
} from './utils';

export const NAMESPACE = 'tickets';

const newTicket = ({ id, ...props }) => ({
  id: id || genId(), // Client side ID for rendering and keeping track of instance
  fulfillmentArtifact: null,
  isValid: null,

  /* SeatTraits */
  buyerRestricted: null,
  row: null,
  seat: null,
  section: null,

  ...props
});

export const ticketController = new Controller({
  namespace: NAMESPACE,
  reducers: [
    { action: 'TICKETS:ADD', handler: handleAdd },
    { action: 'TICKETS:REMOVE', handler: handleRemove },
    { action: 'TICKETS:REMOVE_ALL', handler: handleRemoveAllTickets },
    { action: 'TICKETS:UPDATE', handler: handleUpdate },
    { action: 'TICKETS:SET_QUANTITY', handler: handleSetQuantity },
    { action: 'TICKETS:CHECK_ROW', handler: handleSectionRowCheck },
    { action: 'TICKETS:SET_SECTION_ROW', handler: handleSectionRowSet },
    { action: 'DELIVERY:SET_TICKET_TYPE', handler: handleTicketTypeChange },
    {
      action: 'TICKET_DETAILS:UPDATE_TICKET_TYPE',
      handler: handleRemoveAllTickets
    }, // Deprecated: only present for backward compatibility in moving to multiEvent
    'TICKETS:SET_SEAT_VALUES_REQUIRED'
  ],
  mapStateToProps(state) {
    return {
      ...getLocalState(state),
      allTicketsValid: ticketsSectionRowSeatsValid(state)
    };
  },
  actionCreators: {
    addTicket,
    removeTicket,
    updateTicket,
    removeAllTickets,
    setTicketQuantity,
    checkSectionRow,
    setSectionRow,
    toggleSeatNumbersRequired
  }
});

const initialState = {
  tickets: [],
  master: [],
  row: '',
  section: {},
  seatsValueRequired: true
};

function getLocalState(state = {}) {
  return {
    ...initialState,
    ...state[NAMESPACE]
  };
}

const handleIncomingTicket =
  (state) =>
  (ticket = {}) => {
    const { row, section } = getLocalState(state);

    return {
      row,
      section,
      ...ticket
    };
  };

const isDuplicateOnProp = (tickets, ticket, propName) =>
  !!(
    ticket[propName] &&
    tickets.find(
      ({ id, eventId, ...rest }) =>
        objectHasEventId(ticket, eventId) &&
        id !== ticket.id &&
        rest[propName] === ticket[propName]
    )
  );
const isDuplicateSeat = (tickets, ticket) =>
  isDuplicateOnProp(tickets, ticket, 'seat');

const isDuplicateUniqueNum = (tickets, ticket) =>
  !!(
    ticket.uniqueNumber &&
    tickets.find(
      ({ uniqueNumber, id }) =>
        id !== ticket.id && uniqueNumber === ticket.uniqueNumber
    )
  );

const markDuplicates = (ticket, index, tickets) => ({
  ...ticket,
  isDuplicate: isDuplicateSeat(tickets, ticket),
  isDuplicateUniqueNum: isDuplicateUniqueNum(tickets, ticket)
});

const updateMaster = (master, newTicket) => {
  const eventId = master?.[0]?.eventId;

  return !eventId || objectHasEventId(newTicket, eventId)
    ? [...master, newTicket]
    : master;
};

function handleAdd(state, { type, ...payload }) {
  const { tickets, master = [], ...rest } = getLocalState(state);

  const newTicket = {
    ...handleIncomingTicket(state)(payload.ticket),
    isDuplicate: isDuplicateSeat(tickets, payload.ticket)
  };

  return {
    [NAMESPACE]: {
      ...rest,
      tickets: [...tickets, newTicket],
      master: updateMaster(master, newTicket)
    }
  };
}

function handleRemove(state, { type, ...payload }) {
  const { tickets, master = [], ...rest } = getLocalState(state);
  const seatMatch =
    payload.ticket.seat ||
    tickets.find(({ id }) => id === payload.ticket.id)?.seat;

  return {
    [NAMESPACE]: {
      ...rest,
      tickets: tickets
        .filter(({ id, seat, isDuplicate }) => {
          return !isDuplicate && seatMatch
            ? seatMatch !== seat
            : id !== payload.ticket.id;
        })
        .map(markDuplicates),
      master: master.filter(({ id }) => id !== payload.ticket.id)
    }
  };
}

/**
 * There may be a good strategy to handle storing the tickets per ticket type.
 * That way if the user enters PDF information but then switches to paper they have a blank slate
 * BUT then have the ability to switch back and see the tickets they had entered.
 */
function handleTicketTypeChange(state, { type, eventId }) {
  return handleRemoveAllTickets(state, { type, eventId });
}

function handleRemoveAllTickets(state, { eventId }) {
  const { tickets, master, ...localState } = getLocalState(state);

  return {
    [NAMESPACE]: {
      ...localState,
      tickets: eventId
        ? tickets.filter((ticket) => ticket.eventId != eventId)
        : [],
      master: eventId
        ? master.filter((ticket) => ticket.eventId != eventId)
        : []
    }
  };
}

function handleUpdate(state, { type, ...payload }) {
  const {
    tickets,
    master = [],
    seatsValueRequired,
    ...rest
  } = getLocalState(state);
  const eventOriginCountry = selectEventOriginCountry(state);
  const eventCurrencyCode = selectCurrencyCode(state);

  const seatError =
    (eventOriginCountry === 'GB' &&
      eventCurrencyCode === 'GBP' &&
      checkTicketForSeatRowError(payload.ticket.seat)) ||
    (seatsValueRequired && !payload.ticket.seat);
  const newTicket = {
    ...payload.ticket,
    seatError
  };

  return {
    [NAMESPACE]: {
      ...rest,
      seatsValueRequired,
      tickets: tickets
        .map((ticket) => (ticket.id === newTicket.id ? newTicket : ticket))
        .map(markDuplicates),
      master: master.map((ticket) =>
        ticket.id === newTicket.id ? newTicket : ticket
      )
    }
  };
}

function handleSetQuantity(state, { type, ...payload }) {
  return {
    [NAMESPACE]: {
      ...getLocalState(state),
      tickets: payload.tickets,
      master: payload.tickets
    }
  };
}

function handleSectionRowCheck(state, { type, ...payload }) {
  const localState = getLocalState(state);
  const { section } = localState;
  const eventOriginCountry = selectEventOriginCountry(state);
  const eventCurrencyCode = selectCurrencyCode(state);
  const rowError =
    payload?.validity?.patternMismatch ||
    (eventOriginCountry === 'GB' &&
      eventCurrencyCode === 'GBP' &&
      payload?.row &&
      checkTicketForRowError(payload.row)) ||
    (!section.generalAdmission && !payload?.row);

  return {
    [NAMESPACE]: {
      ...localState,
      rowError
    }
  };
}

/**
 * Handles payload from section / row being set. Payload will contain section / row information
 * including `row`, `section`.
 * Data to be set at top leve in space as well as on each ticket in the space.
 */
function handleSectionRowSet(state, { type, ...payload }) {
  const { tickets, master, section, ...rest } = getLocalState(state);

  const seatsValueRequired = (function () {
    if (typeof payload.section?.generalAdmission === 'boolean') {
      if (section.generalAdmission !== payload.section.generalAdmission) {
        return !payload.section.generalAdmission;
      }
    }

    return rest.seatsValueRequired;
  })();

  return {
    [NAMESPACE]: {
      ...rest,
      ...payload,
      tickets: tickets.map((ticket) => ({
        ...ticket,
        ...payload
      })),
      master: master.map((ticket) => ({
        ...ticket,
        ...payload
      })),
      seatsValueRequired
    }
  };
}

export function addTicket(ticket) {
  return (dispatch, getState) => {
    dispatch({
      type: 'TICKETS:ADD',
      ticket: newTicket(handleIncomingTicket(getState())(ticket))
    });
  };
}

export function removeTicket(ticket) {
  return (dispatch) => {
    dispatch({
      type: 'TICKETS:REMOVE',
      ticket
    });
  };
}

export function updateTicket(ticket) {
  return (dispatch, getState) => {
    const { tickets } = getLocalState(getState());

    if (!ticket.id || !tickets.find(({ id }) => id === ticket.id)) {
      return addTicket(ticket)(dispatch, getState);
    }
    dispatch({
      type: 'TICKETS:UPDATE',
      ticket
    });
  };
}

export function removeAllTickets({ eventId } = {}) {
  return (dispatch, getState, extra) => {
    dispatch({
      type: 'TICKETS:REMOVE_ALL',
      eventId
    });
  };
}

export function setTicketQuantity(num, defaultProps = {}) {
  return (dispatch, getState) => {
    const { tickets } = getLocalState(getState());
    const diff = num - tickets.length;
    if (diff < 0) {
      dispatch({
        type: 'TICKETS:SET_QUANTITY',
        tickets: tickets.slice(0, num),
        count: num
      });
    } else {
      const createTicket = handleIncomingTicket(getState());
      dispatch({
        type: 'TICKETS:SET_QUANTITY',
        tickets: [
          ...tickets,
          ...[...Array(diff)].map(() =>
            newTicket({
              ...createTicket(),
              ...defaultProps
            })
          )
        ],
        count: num
      });
    }
  };
}

export function checkSectionRow(args) {
  return (dispatch) => {
    dispatch({
      type: 'TICKETS:CHECK_ROW',
      ...args
    });
  };
}

export function setSectionRow(args) {
  return (dispatch) => {
    if (!args.row && !args.section) {
      return;
    }
    dispatch({
      type: 'TICKETS:SET_SECTION_ROW',
      ...args
    });
  };
}

export function toggleSeatNumbersRequired(seatsValueRequired) {
  return (dispatch) => {
    dispatch({
      type: 'TICKETS:SET_SEAT_VALUES_REQUIRED',
      seatsValueRequired
    });
  };
}

const getTicketsWithTemplateForEvent = (
  tickets,
  master,
  eventId,
  templateProps = ['seat', 'row', 'section', 'sectionId']
) => {
  const filteredTickets = filterByEventId(tickets, eventId);

  return master.map((template, index) => {
    const ticket = newTicket(filteredTickets[index] || {});

    return {
      ...(ticket || {}),
      ...pick(template, templateProps)
    };
  });
};
const patch =
  (fn, props) =>
  (ticket = {}) =>
    fn({ ...ticket, ...props });
export const withTickets = (Component) => {
  const WithTickets = (props) => {
    const {
      eventId,
      addTicket,
      removeTicket,
      removeAllTickets,
      updateTicket,
      tickets,
      master,
      setTicketQuantity
    } = props;
    const addOnProps = { eventId };
    if (eventId) {
      return (
        <Component
          {...props}
          tickets={getTicketsWithTemplateForEvent(tickets, master, eventId)}
          addTicket={patch(addTicket, addOnProps)}
          removeTicket={patch(removeTicket, addOnProps)}
          removeAllTickets={patch(removeAllTickets, addOnProps)}
          updateTicket={patch(updateTicket, addOnProps)}
          setTicketQuantity={(num) => setTicketQuantity(num, addOnProps)}
        />
      );
    }

    return <Component {...props} />;
  };

  return connect(ticketController)(WithTickets);
};

export default withTickets;
